<template>
  <div class="widget w-forex-matrix-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header hideLinkMode printView :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>
    <c-widget-control-bar>
      <v-form-group :label="$t('white-label.widgets.forexMatrix.currencies')" label-cols="auto">
        <v-form-tags
          multicolor
          :placeholder="currenciesValue.length < 10 ? i18n('enterCurrency') : ''"
          :value="currenciesValue"
          :autocompleteItems="currenciesComplete"
          :autocompleteMinLength="0"
          :maxTags="10"
          add-only-from-autocomplete
          @input="onUpdateCurrencies"
          @inputText="currencyInput = $event"
          detached
          detachedClass="w-forex-matrix-widget"
          sameWidth
        >
          <template slot="autocomplete-item" slot-scope="props">
            <div @click="props.performAdd(props.item)">{{ `${props.item.fullname}` }}</div>
          </template>
          <template slot="tag-left" slot-scope="props">
            <v-flag :countryCode="getCountryCode(props.tag.text)" />
          </template>
        </v-form-tags>
      </v-form-group>
    </c-widget-control-bar>
    <div class="widget-body">
      <c-loader v-if="isLoading" />
      <c-streaming-error :show="hasStreamingError" />
      <b-table-lite
        v-if="!hasError && items.length > 0"
        v-show="!isLoading"
        :fields="fields"
        :items="items"
        dark
        bordered
        fixed
        hover
        small
        sticky-header
        striped
        :aria-label="i18n('widget-title')"
      >
        <template v-for="(field, i) in fields" #[`head(${field.key})`]="data">
          <div :key="i" class="p-1 text-center">
            <v-flag
              v-if="data.label && data.label !== 'currency'"
              :countryCode="getCountryCode(data.label)"
              style="display: inline-block;"
              class="mr-1"
            />
            {{ getFormattedCurrency(data.label) }}
          </div>
        </template>

        <template v-for="(field, i) in fields" #[`cell(${field.key})`]="data">
          <div :key="i" :class="field.key === 'currency' ? 'p-2 text-left' : 'text-center'">
            <div v-if="field.key === 'currency'">
              <v-flag :countryCode="getCountryCode(data.value)" style="display: inline-block;" class="mr-2" />
              <span class="font-weight-bold">{{ getFormattedCurrency(data.value) }}</span>
            </div>
            <c-tick-flash v-else-if="data.value !== '-'" :value="data.value" class="p-2">
              <v-fidlet :data="data.value || ''" type="float" />
            </c-tick-flash>
            <div v-else class="p-2">
              {{ data.value }}
            </div>
          </div>
        </template>
      </b-table-lite>
      <div v-if="hasError" class="text-center">{{ $t('white-label.widgets.forexMatrix.error') }}</div>
    </div>
    <div class="widget-footer">
      {{ $t('white-label.widgets.forexMatrix.widget-footer') }}
    </div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CTickFlash from '@custom/CTickFlash';
import { API } from '@/state/api';
import widgetCommons from '@mixins/widgetCommons';
import streamableFidsFilter from '@/utils/streamableFids';

export default {
  name: 'w-forex-matrix-widget',
  mixins: [widgetCommons],
  components: {
    CLoader,
    CStreamingError,
    CWidgetHeader,
    CWidgetHeaderButton,
    CWidgetControlBar,
    CTickFlash
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      subscriptionId: null,
      isLoading: false,
      hasError: false,
      hasStreamingError: false,
      rics: [],
      currencyInput: '',
      fields: [],
      snapshots: [],
      rawData: null,
      items: []
    };
  },
  computed: {
    allCurrencies() {
      return ['USD', 'CAD', 'EUR', 'JPY', 'GBP', 'AUD', 'CHF', 'CNH', 'HKD', 'NZD'].map(c => ({
        id: c + '=',
        text: c,
        fullname: `${c} (${this.i18n('fullName.' + c)})`
      }));
    },
    currenciesComplete() {
      return this.allCurrencies.filter(c => {
        return c.fullname.toLowerCase().indexOf(this.currencyInput.toLowerCase()) !== -1;
      });
    },
    currencies() {
      return this.$store.state.widget[this.wid].currencies;
    },
    currenciesValue() {
      return this.currencies.map(currency => this.allCurrencies.find(c => c.id === currency));
    }
  },
  methods: {
    onUpdateCurrencies(newTags) {
      const currencies = newTags.map(tags => tags.id);

      this.$store.commit('widget/' + this.wid + '/CURRENCIES_CHANGED', currencies);

      this.fetch();
    },
    fetch() {
      this.isLoading = true;
      this.hasError = false;
      this.hasStreamingError = false;
      this.items = this.fields = [];
      this.snapshots = [];

      if (this.currencies.length) {
        this.getSnapshot()
          .then(this.streamingConnect)
          .then(this.streamingUnsubscribe)
          .then(this.streamingSubscribe)
          .catch(error => {
            console.log(error);
            this.isLoading = false;
            this.hasError = true;
          });
      } else {
        this.streamingUnsubscribe();
        this.isLoading = false;
        this.rawData = null;
        this.items = [];
      }
    },
    sortByOrder(data, prop, order) {
      return data.sort((a, b) => {
        return order.indexOf(a[prop]) - order.indexOf(b[prop]);
      });
    },
    getSnapshot() {
      return API.Rest.get(`/quote/info?rics=${this.currencies}&fids=q.RIC,q._BID,q._ASK,q._CUR_REVERSE`).then(
        response => {
          this.initForexMatrix(response.data.data);
          this.isLoading = false;
        }
      );
    },
    streamingSubscribe() {
      this.subscriptionId = API.Streaming.Quote.subscribe(
        {
          rics: this.currencies, // Rics to be streamed
          fids: streamableFidsFilter(['q.RIC', 'q._BID', 'q._ASK']) // Fids to be streamed
        },
        // Subscribe Callback
        this.handleSubscription,
        // Snapshot Callback
        this.handleSnapshot,
        // Update Callback
        this.handleUpdate
      );
    },
    handleSubscription(_response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming subscription error', error);
      } else {
        this.hasStreamingError = false;
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming snapshot error', error);
        return;
      }

      this.hasStreamingError = false;

      response = response.payload[0];

      // Add new Snapshot to snapshots
      const row = response.data;
      this.snapshots.push(row);
    },
    // eslint-disable-next-line
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
        return;
      }

      this.hasStreamingError = false;

      if (!this.rawData) return;

      response = response.payload[0];

      // Normal update
      try {
        const ric = response.ric;
        const data = response.data;

        // Update rawData to have uptodate-ref-values for calculating
        Object.keys(data).forEach(key => {
          this.rawData[this.itemsMap[ric]][key] = data[key];
        });

        this.items.forEach(row => {
          if (ric === row.currency) {
            Object.keys(row).forEach(key => {
              if (key !== 'currency' && key !== '_cellVariants' && key !== ric) {
                row[key] = this.calculateCrossrates(this.rawData[this.itemsMap[ric]], this.rawData[this.itemsMap[key]]);
              }
            });
          } else {
            row[ric] = this.calculateCrossrates(
              this.rawData[this.itemsMap[row.currency]],
              this.rawData[this.itemsMap[ric]]
            );
          }
        });
      } catch (e) {
        console.error(e);
      }
    },
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
        } else {
          resolve();
        }
      });
    },
    streamingUnsubscribe() {
      return new Promise((resolve, reject) => {
        if (this.subscriptionId !== null) {
          API.Streaming.Quote.unsubscribe(this.subscriptionId, (e, err) => {
            if (err) {
              reject(err);
            } else {
              this.subscriptionId = null;
              resolve(e);
            }
          });
        } else {
          resolve();
        }
      });
    },
    initForexMatrix(data) {
      this.rawData = null;
      this.fields = [];
      this.itemsMap = null;
      this.matrix = [];
      this.items = [];
      this.rawData = this.sortByOrder(data, 'q.RIC', this.currencies);
      this.fields = this.createFields();
      this.itemsMap = this.createItemsMap(this.rawData);
      this.matrix = this.createMatrix(this.rawData);
      this.items = this.createItems();
    },
    createFields() {
      const result = [{ key: 'currency', label: '' }];

      this.rawData.forEach(data => {
        result.push({
          key: data['q.RIC'],
          label: data['q.RIC'],
          tdClass: 'text-center'
        });
      });

      return result;
    },
    createMatrix(data) {
      const matrix = [];

      data.forEach((row, i) => {
        matrix[i] = [];
        data.forEach(row2 => {
          matrix[i].push({
            [row2['q.RIC']]: row['q.RIC'] !== row2['q.RIC'] ? this.calculateCrossrates(row, row2) : '-',
            currency: row['q.RIC'],
            _cellVariants: { [row['q.RIC']]: 'cross-cell' }
          });
        });
      });

      return matrix;
    },
    calculateCrossrates(cur1, cur2) {
      // cur1 in USD
      const fak1 = cur1['q._CUR_REVERSE'] === 'Y' ? cur1['q._BID'] : 1 / cur1['q._ASK'];

      // 1 USD in cur2
      const fak2 = cur2['q._CUR_REVERSE'] === 'Y' ? 1 / cur2['q._ASK'] : cur2['q._BID'];

      return (fak1 * fak2).toFixed(4);
    },
    createItems() {
      return this.matrix.map(row => {
        return row.reduce((total, item) => {
          return {
            ...total,
            ...item
          };
        }, {});
      });
    },
    createItemsMap(items) {
      let result = {};
      items.forEach((item, i) => {
        result[item['q.RIC']] = i;
      });
      return result;
    },
    getCountryCode(currency) {
      return currency.substring(0, 2).toLowerCase();
    },
    getFormattedCurrency(currency) {
      return currency.replace('=', '');
    }
  },
  mounted() {
    this.fetch();
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
  }
};
</script>
<style lang="scss">
@import '~@/styles/widgets/forex-matrix-widget';
</style>
